<template>
    <li :class="{ active: isActive, disabled: isDisabled }" @mouseenter="mouseEnter" @mouseleave="mouseLeave">
        <router-link
            v-if="isMenu && link.path"
            :to="link.path"
            class="nav-link sidebar-menu-item"
            @click.native="collapseMenu"
            :class="{ 'disabled-link': isDisabled, 'expanded-menu-item': !collapsed }"
        >
            <i v-if="link.icon !== ''" class="material-symbols-outlined">{{ link.icon }}</i>
            <i v-else :class="link.icon_fa"></i>
            <p>
                {{ link.name }}
            </p>
            <b class="caret"></b>
        </router-link>
        <a
            v-else-if="isMenu"
            href="#"
            class="nav-link sidebar-menu-item"
            @click.prevent="collapseMenu"
            :class="{ 'disabled-link': isDisabled, 'expanded-menu-item': !collapsed }"
        >
            <i v-if="link.icon !== ''" class="material-symbols-outlined">{{ link.icon }}</i>
            <i v-else :class="link.icon_fa"></i>
            <p>
                {{ link.name }}
            </p>
            <b class="caret"></b>
        </a>

        <collapse-transition>
            <div v-if="$slots.default || this.isMenu" v-show="!collapsed">
                <ul class="nav">
                    <slot></slot>
                </ul>
            </div>
        </collapse-transition>

        <slot name="title" v-if="children.length === 0 && !$slots.default && link.path">
            <a
                v-if="link.isRoute === false"
                :href="link.path"
                :target="link.target"
                class="nav-link"
                :class="{ 'has-end-icon': link.endIcon, 'sidebar-nested-menu-item': isNested }"
                @click="linkClick"
            >
                <i v-if="link.icon !== ''" class="material-symbols-outlined">{{ link.icon }}</i>
                <i v-else :class="link.icon_fa"></i>
                <p>
                    {{ link.name }}
                </p>
                <i v-if="link.endIcon !== ''" class="material-symbols-outlined end-icon">{{ link.endIcon }}</i>
            </a>
            <router-link
                v-else
                :to="link.path"
                class="nav-link"
                :class="{ active: isActive, 'sidebar-nested-menu-item': isNested }"
                @click.native="linkClick"
            >
                <i v-if="link.icon !== ''" class="material-symbols-outlined">{{ link.icon }}</i>
                <i v-else :class="link.icon_fa"></i>
                <p>{{ link.name }}</p>
            </router-link>
        </slot>
    </li>
</template>
<script>
import { CollapseTransition } from 'vue2-transitions';

export default {
    name: 'sidebar-item',
    components: {
        CollapseTransition,
    },
    props: {
        menu: {
            type: Boolean,
            default: false,
        },
        link: {
            type: Object,
            default: () => {
                return {
                    name: '',
                    path: '',
                    disableCollapse: Boolean,
                    children: [],
                    icon: '',
                    icon_fa: '',
                    endIcon: '',
                };
            },
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    provide() {
        return {
            addLink: this.addChild,
            removeLink: this.removeChild,
        };
    },
    inject: {
        addLink: { default: null },
        removeLink: { default: null },
        autoClose: {
            default: true,
        },
    },
    data() {
        return {
            children: [],
            collapsed: true,
            openedByHover: false,
        };
    },
    computed: {
        isNested() {
            return !!this.addLink;
        },
        isDisabled() {
            return this.disabled;
        },
        linkPrefix() {
            if (this.link.name) {
                let words = this.link.name.split(' ');
                return words.map((word) => word.substring(0, 1)).join('');
            }
            return false;
        },
        isMenu() {
            return this.children.length > 0 || this.menu === true;
        },
        isActive() {
            if (this.$route && this.link.path) {
                // Exact match or sub-path with slash
                return this.$route.path === this.link.path || this.$route.path.startsWith(this.link.path + '/');
            }
            return false;
        },
    },
    methods: {
        addChild(item) {
            const index = this.$slots.default.indexOf(item.$vnode);
            this.children.splice(index, 0, item);
        },
        removeChild(item) {
            const tabs = this.children;
            const index = tabs.indexOf(item);
            tabs.splice(index, 1);
        },
        elementType(link, isParent = true) {
            if (link.isRoute === false) {
                return isParent ? 'li' : 'a';
            } else {
                return 'router-link';
            }
        },
        linkAbbreviation(name) {
            const matches = name.match(/\b(\w)/g);
            return matches.join('');
        },
        linkClick() {
            if (!this.addLink && !this.link.disableCollapse) {
                this.$sidebar.collapseAllMenus();
            }

            if (this.autoClose && this.$sidebar && this.$sidebar.showSidebar === true) {
                this.$sidebar.displaySidebar(false);
            }
        },
        mouseEnter() {
            if (!this.isMenu || this.isDisabled) return;
            if (this.collapsed) {
                this.collapsed = false;
                this.openedByHover = true;
            }
        },
        mouseLeave() {
            if (this.openedByHover) {
                this.collapsed = true;
                this.openedByHover = false;
            }
        },
        collapseMenu() {
            if (this.isDisabled) return; // Prevent collapse when disabled

            if (this.openedByHover) {
                this.openedByHover = false;
                if (!this.link.disableCollapse) {
                    this.$sidebar.addSidebarLink(this);
                    this.$sidebar.collapseAllMenus();
                }
                this.collapsed = false; // Ensure it stays open
                return;
            }

            if (this.collapsed && !this.link.disableCollapse) {
                this.$sidebar.addSidebarLink(this);
                this.$sidebar.collapseAllMenus();
            }

            this.collapsed = !this.collapsed;
        },
        collapseSubMenu(link) {
            link.collapsed = !link.collapsed;
        },
    },
    mounted() {
        if (this.addLink) {
            this.addLink(this);
        }
        if (this.link.collapsed !== undefined) {
            this.collapsed = this.link.collapsed;
        }
        if (this.isActive && this.isMenu) {
            this.collapsed = false;
        }
    },
    destroyed() {
        this.$sidebar.removeSidebarLink(this);
        if (this.$el && this.$el.parentNode) {
            this.$el.parentNode.removeChild(this.$el);
        }
        if (this.removeLink) {
            this.removeLink(this);
        }
    },
};
</script>
<style lang="scss" scoped>
.disabled {
    opacity: 0.5;
    pointer-events: none;
}

.disabled-link {
    cursor: default;
}
.sidebar-menu-item {
    cursor: pointer;
}

.nav-link {
    .end-icon {
        position: absolute !important;
        right: 15px !important;
        font-size: calc(20px * var(--sb-scale, 1)) !important;
    }

    &.has-end-icon {
        display: flex !important;
        align-items: center !important;
        position: relative !important;

        p {
            margin: 0 !important;
        }
    }
}

div .nav {
    margin-left: 0 !important;
    background-color: rgba(0, 0, 0, 0.03);
    padding-top: calc(5px * var(--sb-scale, 1));
    padding-bottom: calc(5px * var(--sb-scale, 1));
    list-style: none;
    padding-left: 0;
}

/* Target links inside the submenu */
div .nav ::v-deep .nav-link {
    padding-left: 10px !important;
}

.nav-link {
    display: flex !important;
    align-items: center !important;
    position: relative; /* Ensure caret is positioned relative to this link */

    p {
        margin: 0 !important;
        line-height: inherit;
        white-space: nowrap;
    }

    i {
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }
}

.expanded-menu-item {
    background-color: rgba(200, 200, 200, 0.2) !important;
}

.sidebar-nested-menu-item {
    margin: 0 !important;
}
</style>
